import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { authBaseUrl } from '../../config/runtime';
import './AttendantManager.css';

const AttendantManager = () => {
  const token = localStorage.getItem('token');
  const companyCode = localStorage.getItem('companyCode') || '';
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [form, setForm] = useState({
    password: '',
    name: '',
    email: '',
    phone: '',
    role: 'atendente',
    department: 'operacional',
    active: true,
  });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');

  const getApiError = (err, fallback) => {
    const apiMessage = err?.response?.data?.message || err?.response?.data?.msg;
    return apiMessage || fallback;
  };

  const resetForm = () => {
    setForm({ password: '', name: '', email: '', phone: '', role: 'atendente', department: 'operacional', active: true });
    setEditing(null);
  };

  const loadUsers = useCallback(async () => {
    if (!token) {
      setError('Sessão inválida. Faça login novamente.');
      return;
    }
    setLoadingUsers(true);
    setError('');
    try {
      const res = await axios.get(`${authBaseUrl}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(companyCode ? { 'X-Company-Code': companyCode } : {})
        }
      });
      setUsers(res.data || []);
    } catch (err) {
      setError(getApiError(err, 'Falha ao carregar atendentes.'));
    } finally {
      setLoadingUsers(false);
    }
  }, [token, companyCode]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadUsers();
    }, 15000);
    return () => clearInterval(intervalId);
  }, [loadUsers]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    if (!token) {
      setError('Sessão inválida. Faça login novamente.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (editing) {
        await axios.put(`${authBaseUrl}/users/${editing}`, form, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(companyCode ? { 'X-Company-Code': companyCode } : {})
          }
        });
      } else {
        await axios.post(`${authBaseUrl}/users`, form, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(companyCode ? { 'X-Company-Code': companyCode } : {})
          }
        });
      }
      resetForm();
      await loadUsers();
    } catch (err) {
      setError(getApiError(err, 'Falha ao salvar atendente.'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditing(user.id);
    setForm({
      password: '',
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'atendente',
      department: user.department || 'operacional',
      active: user.active,
    });
  };

  const handleDeactivate = async (id) => {
    if (!token) {
      setError('Sessão inválida. Faça login novamente.');
      return;
    }
    setError('');
    try {
        await axios.delete(`${authBaseUrl}/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(companyCode ? { 'X-Company-Code': companyCode } : {})
        }
      });
      await loadUsers();
    } catch (err) {
      setError(getApiError(err, 'Falha ao desativar atendente.'));
    }
  };

  const stats = useMemo(() => {
    const total = users.length;
    const online = users.filter((user) => user.active && user.is_online).length;
    const active = users.filter((user) => user.active).length;
    const inactive = total - active;
    const admins = users.filter((user) => user.role === 'admin').length;
    return { total, online, active, inactive, admins };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((user) => {
      const byStatus =
        statusFilter === 'todos' ||
        (statusFilter === 'ativos' && user.active) ||
        (statusFilter === 'inativos' && !user.active);
      if (!byStatus) {
        return false;
      }
      if (!query) {
        return true;
      }
      const target = `${user.name || ''} ${user.username || ''} ${user.email || ''}`.toLowerCase();
      return target.includes(query);
    });
  }, [users, search, statusFilter]);

  const getRoleLabel = (role) => {
    if (role === 'admin') return 'Administrador';
    if (role === 'supervisor') return 'Supervisor';
    return 'Atendente';
  };

  const getDepartmentLabel = (department) => {
    const normalized = String(department || '').toLowerCase();
    if (normalized === 'comercial') return 'Comercial';
    if (normalized === 'tecnico') return 'Tecnico';
    if (normalized === 'ti') return 'TI';
    if (normalized === 'gestao') return 'Gestao';
    return 'Operacional';
  };

  const getInitials = (user) => {
    const source = (user.name || user.username || '?').trim();
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const formatCreatedAt = (value) => {
    if (!value) return 'Data indisponivel';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Data indisponivel';
    return `Criado em ${date.toLocaleDateString('pt-BR')}`;
  };

  const formatLastSeenAt = (value) => {
    if (!value) return 'Sem atividade';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Sem atividade';
    return `Visto ${date.toLocaleString('pt-BR')}`;
  };

  const getPresenceLabel = (user) => {
    if (!user.active) return 'Inativo';
    return user.is_online ? 'Online' : 'Offline';
  };

  const getPresenceClass = (user) => {
    if (!user.active) return 'is-inactive';
    return user.is_online ? 'status-connected' : 'status-disconnected';
  };

  return (
    <div className="attendant-manager">
      <div className="attendant-topbar">
        <div className="attendant-header">
          <h2>Gestão de Atendentes</h2>
          <p>Controle acessos, niveis e responsabilidades da equipe.</p>
        </div>
        <div className="attendant-metrics">
          <div className="metric-card">
            <span>Total</span>
            <strong>{stats.total}</strong>
            <small>usuarios</small>
          </div>
          <div className="metric-card">
            <span>Online</span>
            <strong>{stats.online}</strong>
            <small>tempo real</small>
          </div>
          <div className="metric-card">
            <span>Admins</span>
            <strong>{stats.admins}</strong>
            <small>com controle total</small>
          </div>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="attendant-grid">
        <div className="attendant-card attendant-form-card">
          <div className="attendant-card-head">
            <h3>{editing ? 'Editar atendente' : 'Novo atendente'}</h3>
            <p>{editing ? 'Atualize os dados e salve as alteracoes.' : 'Crie um usuario com perfil de acesso.'}</p>
          </div>
          <div className="attendant-form">
            <label>
              <span>Senha</span>
              <input name="password" placeholder={editing ? 'Preencha para alterar a senha' : 'Digite uma senha'} type="password" value={form.password} onChange={handleChange} />
            </label>
            <label>
              <span>Nome completo</span>
              <input name="name" placeholder="Ex.: Joao Silva" value={form.name} onChange={handleChange} />
            </label>
            <label>
              <span>E-mail</span>
              <input name="email" placeholder="Ex.: joao@empresa.com" value={form.email} onChange={handleChange} />
            </label>
            <label>
              <span>Telefone</span>
              <input name="phone" placeholder="Ex.: 47999990000" value={form.phone} onChange={handleChange} />
            </label>
            <label>
              <span>Perfil</span>
              <select name="role" value={form.role} onChange={handleChange}>
                <option value="admin">Administrador</option>
                <option value="supervisor">Supervisor</option>
                <option value="atendente">Atendente</option>
              </select>
            </label>
            <label>
              <span>Setor</span>
              <select name="department" value={form.department} onChange={handleChange}>
                <option value="operacional">Operacional</option>
                <option value="tecnico">Tecnico</option>
                <option value="ti">TI</option>
                <option value="gestao">Gestao</option>
                <option value="comercial">Comercial</option>
              </select>
            </label>
            <label className="toggle">
              <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
              Ativo
            </label>
            <button onClick={handleSave} disabled={loading}>{loading ? 'Salvando...' : 'Salvar atendente'}</button>
            {editing && <button className="ghost" onClick={resetForm} disabled={loading}>Cancelar edicao</button>}
          </div>
        </div>

        <div className="attendant-card attendant-list-card">
          <div className="attendant-card-head attendant-card-head--row">
            <div>
              <h3>Equipe</h3>
              <p>{stats.inactive > 0 ? `${stats.inactive} usuario(s) inativo(s)` : 'Todos os usuarios estao ativos'}</p>
            </div>
            <div className="attendant-list-tools">
              <input
                className="attendant-search"
                placeholder="Buscar por nome, usuario ou e-mail"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className="attendant-filters">
                <button
                  className={statusFilter === 'todos' ? 'active' : ''}
                  onClick={() => setStatusFilter('todos')}
                >
                  Todos
                </button>
                <button
                  className={statusFilter === 'ativos' ? 'active' : ''}
                  onClick={() => setStatusFilter('ativos')}
                >
                  Ativos
                </button>
                <button
                  className={statusFilter === 'inativos' ? 'active' : ''}
                  onClick={() => setStatusFilter('inativos')}
                >
                  Inativos
                </button>
              </div>
            </div>
          </div>
          <div className="attendant-list">
            {loadingUsers && <p>Carregando atendentes...</p>}
            {!loadingUsers && filteredUsers.length === 0 && <p>Nenhum resultado para os filtros aplicados.</p>}
            {filteredUsers.map((user) => (
              <div key={user.id} className={`attendant-item ${user.active ? '' : 'is-inactive'}`}>
                <div className="attendant-user">
                  <div className={`attendant-avatar role-${user.role || 'atendente'}`}>{getInitials(user)}</div>
                  <div className="attendant-user-main">
                    <div className="attendant-user-top">
                      <strong>{user.name || user.username}</strong>
                      <span className={`role-pill role-${user.role || 'atendente'}`}>{getRoleLabel(user.role)}</span>
                      <span className="role-pill role-department">{getDepartmentLabel(user.department)}</span>
                    </div>
                    <small>{user.email || 'Sem e-mail cadastrado'}</small>
                    <div className="attendant-meta">
                      <span>{user.phone || 'Sem telefone'}</span>
                      <span>{user.active ? 'Ativo' : 'Inativo'}</span>
                      <span>{formatLastSeenAt(user.last_seen_at)}</span>
                      <span>{formatCreatedAt(user.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="attendant-actions">
                  <span className={`status-pill ${getPresenceClass(user)}`}>
                    {getPresenceLabel(user)}
                  </span>
                  <button className="ghost" onClick={() => handleEdit(user)}>Editar</button>
                  <button className="danger" onClick={() => handleDeactivate(user.id)} disabled={!user.active}>Desativar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendantManager;
